import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { PhoneInput } from "../../../_common";
import { Form, Input, Select, Popconfirm } from "antd";
import { RuleObject } from "antd/lib/form";
import srv from "../../../../services";
import {
  BaseForm,
  Col,
  SubmitButton,
  Title,
  Alert,
} from "../AppealForm.styled";
import ModalNotification from "../ModalNotification/ModalNotification";

export default function AppealAskQuestion() {
  const {
    form,
    isSubmiting,
    error,
    answerTypes,
    topics,
    validateAgreement,
    visible,
    handleCancel,
    push,
    ...formProps
  } = useForm();
  const contracts = useContracts();
  const value =
    sessionStorage.getItem("questionFlag") === "true"
      ? JSON.parse(sessionStorage.getItem("questionForm"))
      : {};

  return (
    <>
      <Title>Задать вопрос ответственному по договору</Title>
      <BaseForm
        form={form}
        layout="vertical"
        scrollToFirstError
        initialValues={value}
        {...formProps}
      >
        <Col>
          <Form.Item
            name="contractId"
            label="№ договора"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="children"
              placeholder="Выберите № договора"
            >
              {contracts.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.printNumber}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="typeAppeals"
            label="Тема"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="children"
              placeholder="Выберите тему"
            >
              {topics.map((item) => (
                <Select.Option key={item.id} value={item.id} title={item.tooltips || 'подробного описания нет'}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="responseMethod"
            label="Выберите удобный способ получения ответа"
            rules={[{ required: true }]}
          >
            <Select>
              {answerTypes.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            name="lastName"
            label="Фамилия"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="firstName" label="Имя" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="middleName"
            label="Отчество"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Номер телефона"
            rules={[{ required: true }]}
          >
            <PhoneInput />
          </Form.Item>
        </Col>

        <Col flex={1.3}>
          <Form.Item
            name="message"
            label="Сообщение"
            rules={[{ required: true }]}
          >
            <Input.TextArea autoSize={{ minRows: 18, maxRows: 22 }} />
          </Form.Item>

          {!!error && <Alert type="error" showIcon message={error} />}
          <Popconfirm
            title="Вы уверены?"
            okButtonProps={{ danger: true }}
            okText="Да"
            cancelText="Отмена"
            onConfirm={() => form.submit()}
            disabled={isSubmiting}
          >
            <SubmitButton loading={isSubmiting}>Отправить</SubmitButton>
          </Popconfirm>
        </Col>
      </BaseForm>
      <ModalNotification
        handleCancel={handleCancel}
        visible={visible}
        push={() => push("/feedback/appeals-question")}
      >
        <Alert message="Вопрос успешно создан" type="success" />
        <p>
          Для перехода к списку ранее заданных вопросов нажмите кнопку "Перейти"
        </p>
      </ModalNotification>
    </>
  );
}

function useForm() {
  const contragent =
    useQueryClient().getQueryData<Models.Contragent>("contragent");
  const answerTypes =
    useQueryClient().getQueryData<Models.Dict[]>("answerTypes");
  const topics = useQueryClient().getQueryData<Models.Dict[]>("topics");
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const { push } = useHistory();

  const showModal = () => {
    setVisible(true);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const {
    mutate: save,
    isLoading,
    isSuccess,
    error,
  } = useMutation(srv.saveAppealAskQuestion);

  const onFinish = useCallback(
    (values) => {
      save({ ...values, uidlk: contragent.uid });
    },
    [save, contragent]
  );

  useEffect(() => {
    if (isSuccess) {
      showModal();
      sessionStorage.removeItem("questionForm");
    }
    return () => {
      sessionStorage.setItem(
        "questionForm",
        JSON.stringify(form.getFieldsValue(true))
      );
      sessionStorage.setItem("questionFlag", JSON.stringify(!isSuccess));
    };
  }, [isSuccess, form]);

  const validateAgreement = useCallback(
    (): RuleObject => ({
      validator(_, value) {
        if (value) return Promise.resolve();
        return Promise.reject("Обязательное поле");
      },
    }),
    []
  );

  return {
    form,
    answerTypes,
    topics,
    isSubmiting: isLoading,
    error,
    validateAgreement,
    onFinish,
    visible,
    handleCancel,
    push,
  };
}

function useContracts() {
  const contracts =
    useQueryClient().getQueryData<Models.Contract[]>("contracts");

  return useMemo(() => {
    return contracts?.filter((c) => c.state !== "Расторгнут");
  }, [contracts]);
}
